// Data-viz palettes. The design tokens cover brand, surface and status
// colors but have no chart ramp, and a 7-slice donut needs hues the token
// set simply does not contain. So these are validated separately against
// the app's real dark surface (--color-void, #0d0d0d) instead of eyeballed.
//
// Everything below was checked with the dataviz validator, dark mode,
// surface #0d0d0d. Re-run it before touching a hex.

// Categorical = identity (which category), never magnitude.
//
// The ORDER is the colorblind-safety mechanism, not decoration. A donut is
// a closed ring, so slot 7 touches slot 1 — the documented linear order
// fails there (violet↔blue: normal-vision ΔE 9.8, under the hard floor of
// 15). This order was picked by enumerating all 720 cyclic orderings of
// the seven hues and keeping the best that clears every gate around the
// full ring: worst adjacent CVD ΔE 9.4, worst normal-vision ΔE 19.3, all
// slots >= 3:1 on the surface. Do not reorder without re-running the
// search — warmer-first variants were tried and fail CVD at ΔE 1.6-2.7.
export const CATEGORICAL_SLOTS = [
  '#3987e5', // blue
  '#d95926', // orange
  '#199e70', // aqua
  '#9085e9', // violet
  '#c98500', // yellow
  '#d55181', // magenta
  '#008300', // green
] as const;

// The absence of an identity: unknown / "otros" buckets. This is the
// text-tertiary token (6.65:1 on the surface). It sits below the chroma
// floor on purpose — like a diverging midpoint, it has to read as "no
// category" rather than as another series.
export const NEUTRAL_SLOT = '#979797';

// Ordinal = position in an ordered sequence (follower tiers). One hue,
// monotone light -> dark, so a bigger audience reads as a deeper red. Six
// steps is the ceiling for a single hue: adjacent ΔL >= 0.06 and the
// darkest step still clears 2:1 on the surface (2.52:1). The eleven raw
// `rank` values cannot be stepped this way (ΔL 0.05), which is why
// follower ranks are folded into six tiers before they reach a color.
// Hue is the brand red (OKLCH h 25.7deg), chroma tapered at the light end
// so the pale steps do not read as pink noise.
export const FOLLOWER_RAMP = [
  '#ffc4bb',
  '#ffa59c',
  '#e9867d',
  '#d0675f',
  '#b64842',
  '#9d2525',
] as const;

// Slots are assigned by the entity's fixed position, never by its rank in
// the current data, so a category keeps its hue when the numbers move.
export function categoricalSlot(index: number): string {
  return CATEGORICAL_SLOTS[index] ?? NEUTRAL_SLOT;
}

export function followerRampStep(index: number): string {
  return FOLLOWER_RAMP[index] ?? NEUTRAL_SLOT;
}
